import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import * as appHelpers from '@src/services/helpers/appHelpers';
import { CollectionView } from 'common';
import { Pathname } from '@src/types/Browsing';
import { Hide } from '@src/components/Hide';
import { Loader } from '@src/components/Loader';
import { style, classes } from 'typestyle';
import { Row } from '@src/components/Utils';
import { CollectionThumb } from '@src/pages/Collection/components/CollectionThumb';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

interface IProps {
  category: string;
  list: CollectionView[];
  isLoading: boolean;
  onCollectionsChange?: () => void;
}

const CollectionsListContent = ({ category, list, isLoading, onCollectionsChange }: IProps) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({
    id: category,
    data: { type: 'category-container', category },
  });

  const updateScrollControls = useCallback(() => {
    const node = scrollContainerRef.current;
    if (!node) {
      return;
    }
    const maxScrollLeft = node.scrollWidth - node.clientWidth;
    const nextCanScrollLeft = node.scrollLeft > 4;
    const nextCanScrollRight = maxScrollLeft - node.scrollLeft > 4;
    if (nextCanScrollLeft !== canScrollLeft) {
      setCanScrollLeft(nextCanScrollLeft);
    }
    if (nextCanScrollRight !== canScrollRight) {
      setCanScrollRight(nextCanScrollRight);
    }
  }, [canScrollLeft, canScrollRight]);

  useEffect(() => {
    updateScrollControls();
  }, [updateScrollControls, list.length]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollControls);
    return () => window.removeEventListener('resize', updateScrollControls);
  }, [updateScrollControls]);

  const handleScroll = useCallback(() => {
    updateScrollControls();
  }, [updateScrollControls]);

  const scrollBy = useCallback(
    (direction: 'left' | 'right') => {
      const node = scrollContainerRef.current;
      if (!node) {
        return;
      }
      const distance = Math.max(node.clientWidth * 0.8, 150);
      node.scrollBy({ left: direction === 'left' ? -distance : distance, behavior: 'smooth' });
    },
    []
  );

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      setDroppableNodeRef(node);
    },
    [setDroppableNodeRef]
  );

  return (
    <div className={scrollWrapperClass}>
      {canScrollLeft && (
        <button
          type='button'
          aria-label='Scroll collections left'
          className={`${scrollArrowClass} ${scrollArrowLeftClass}`}
          onClick={() => scrollBy('left')}
        >
          <KeyboardArrowLeftIcon fontSize='small' />
        </button>
      )}
      <div
        className={classes(collectionsListClass, isOver && collectionsListActiveClass)}
        ref={setRefs}
        onScroll={handleScroll}
      >
        {list.length === 0 ? (
          <div data-empty={window.T('create.collections.empty')}>
            <Hide hide={!isLoading}>
              <Row className={loaderClass}>
                <Loader />
              </Row>
            </Hide>
          </div>
        ) : null}
        {list.length > 0 && (
          <SortableContext
            id={category}
            items={list.map((collection) => collection._id)}
            strategy={horizontalListSortingStrategy}
          >
            {list.map((collection) => (
              <SortableCollectionCard
                key={collection._id}
                collection={collection}
                onCollectionsChange={onCollectionsChange}
              />
            ))}
          </SortableContext>
        )}
      </div>
      {canScrollRight && (
        <button
          type='button'
          aria-label='Scroll collections right'
          className={`${scrollArrowClass} ${scrollArrowRightClass}`}
          onClick={() => scrollBy('right')}
        >
          <KeyboardArrowRightIcon fontSize='small' />
        </button>
      )}
    </div>
  );
};

const loaderClass = style({
  marginTop: '33%',
  justifyContent: 'center',
});

const padding = '6px';
const CARD_WIDTH = 168;
const CARD_HEIGHT = 249;

const scrollWrapperClass = style({
  position: 'relative',
});

const collectionsListClass = style({
  overflowX: 'auto',
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 1,
  gap: '1px',
  padding: `0 ${padding}`,
  scrollBehavior: 'smooth',
  scrollSnapType: 'x proximity',
  scrollbarWidth: 'none',
  "-ms-overflow-style": 'none',
  $nest: {
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '> div:empty': {
      height: '200px',
      $nest: {
        '&:before': {
          content: 'attr(data-empty)',
          display: 'block',
          textAlign: 'center',
          marginTop: 75,
          color: '#646464',
          fontFamily: 'Public Sans, sans-serif',
          fontSize: 25,
        },
      },
    },
  },
});

const collectionsListActiveClass = style({});

const scrollArrowClass = style({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  width: 34,
  height: 34,
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(6,6,6,0.7)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  zIndex: 2,
  padding: 0,
});

const scrollArrowLeftClass = style({
  left: 4,
});

const scrollArrowRightClass = style({
  right: 4,
});
const collectionContainerClass = style({
  flex: `0 0 ${CARD_WIDTH}px`,
  width: CARD_WIDTH,
  height: CARD_HEIGHT,
  padding,
  display: 'flex',
  alignItems: 'stretch',
  $nest: {
    '@media only screen and (max-width: 800px)': {
      flex: `0 0 104px`,
      width: 104,
      height: 187,
    },
  },
});
const collectionThumbClass = style({
  height: '100%',
  width: '100%',
});

const collectionDraggingClass = style({
  opacity: 0.65,
  cursor: 'grabbing',
});

interface CollectionCardPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  collection: CollectionView;
  onCollectionModified?: () => void;
}

export const CollectionCardPreview = React.forwardRef<HTMLDivElement, CollectionCardPreviewProps>(
  ({ collection, className, style: inlineStyle, onCollectionModified, ...rest }, ref) => (
    <div ref={ref} className={classes(collectionContainerClass, className)} style={inlineStyle} {...rest}>
      <CollectionThumb
        collection={collection}
        full
        className={collectionThumbClass}
        onClick={() => appHelpers.setRoute(`/collections/${collection._id}` as Pathname)}
        backgroudImageUrl={collection.preview_media_url ?? undefined}
        onDeleted={onCollectionModified}
      />
    </div>
  )
);
CollectionCardPreview.displayName = 'CollectionCardPreview';

interface SortableCollectionCardProps {
  collection: CollectionView;
  onCollectionsChange?: () => void;
}

const SortableCollectionCard = ({ collection, onCollectionsChange }: SortableCollectionCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: collection._id,
    data: { type: 'collection-card', collectionId: collection._id },
  });

  return (
    <CollectionCardPreview
      ref={setNodeRef}
      collection={collection}
      className={classes(isDragging && collectionDraggingClass)}
      onCollectionModified={onCollectionsChange}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
    />
  );
};

export default CollectionsListContent;
